import { Product, ProductAttribute } from '@@types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Thumbs } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useIntersectionObserver, useMediaQuery } from 'usehooks-ts'


import { formatPriceAmount } from '@entities/Product/helpers'
import ProductProperties from '@entities/Product/ui/product-properties/ui'

import ControlCart from '@features/cart/ui/control/CartControl'
import { FavoriteButton } from '@features/favorite'
import { useGalleryModal, useGalleryStore } from '@features/gallery'
import { InStock } from '@features/in-stock'



import TabsSwitcher from '@widgets/modal-delivery/ui/TabsSwitcher'
import { useFormatShelfLife } from '@widgets/product-detail/hooks/useFormatShelfLife'

import { URL_PAGE_CATALOG } from '@shared/contants'
import { useIsAdult } from '@shared/hooks'
import { cn } from '@shared/lib/cn'
import Badge from '@shared/ui/badge/Badge'
import BadgeGroup from '@shared/ui/badge/group'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Button from '@shared/ui/button'
import ImageWithBg from '@shared/ui/image-with-bg'
import RatingPreview from '@shared/ui/rating/preview/preview'
import { formatPrice } from '@shared/uttils'
import { getProductGalleryImages } from '@shared/uttils/getGalleryImages'
import getNoun from '@shared/uttils/getNoun'

import ProductReviews from './ProductReviews'
import ShopsWithProduct from './ShopsWithProduct'
import StickyCard from './sticky-card/StickyCard'


import styles from './ProductDetail.module.scss'

import type { Swiper as SwiperType } from 'swiper'



interface ProductDetailProps {
  product: Product;
}

interface AttributeItemProps {
  attr: ProductAttribute;
}

function AttributeItem (props: AttributeItemProps) {
  const { attr } = props

  return (
    <div className={styles.about__subitem}>
      <h3 className={styles['about__subitem-name']}>{attr?.name}</h3>
      <span className={styles['about__subitem-line']}></span>
      <span className={styles['about__subitem-value']}>{attr?.value}</span>
    </div>
  )
}

function About (props: ProductDetailProps) {
  const shellLife = useFormatShelfLife()
  const { t: tp } = useTranslation('product')

  const { product } = props

  return (
    <div className={styles.about}>
      {product.short_description && (
        <div className={cn(styles.about__item, styles.large)}>
          <h3 className={styles.about__title}>{tp('product.description')}</h3>
          <p className={styles.about__desc}>{product.short_description}</p>
        </div>
      )}

      {product.attributes && !!product.attributes.length && (
        <div className={styles.about__item}>
          <h3 className={styles.about__title}>{tp('product.info')}</h3>
          {product.attributes.map((item) => (
            <AttributeItem attr={item as ProductAttribute} key={item?.name} />
          ))}
        </div>
      )}

      {product.productProperties?.shelfLife && (
        <div className={styles.about__item}>
          <h3 className={styles.about__title}>
            {tp('storage.conditions')}
          </h3>
          <AttributeItem
            attr={
              {
                name: tp('storage.maxPeriod'),
                value: shellLife(
                  product.productProperties?.shelfLife as string,
                ),
              } as ProductAttribute
            }
          />
        </div>
      )}
    </div>
  )
}

function ProductTabs ({ product }: { product: Product }) {
  const { t: tp } = useTranslation('product')
  
  const tabs = [
    { label: tp('tabs.about'), value: 'about_product' },
    {
      label: (
        <span className={styles['tab-reviews']}>
          <span>{tp('tabs.reviews')}</span>
          <span className={styles['tab-rating']}>
            <RatingPreview
              className={styles['root__rating-star']}
              classNameText={styles['root__rating-value']}
              value={product.average_rating as number}
            />
            <span className={styles['root__rating-count']}>
              {product.review_count}{' '}
              {getNoun(
                product.review_count as number,
                tp('productReview.1'),
                tp('productReview.2'),
                tp('productReview.3'),
              )}
            </span>
          </span>
        </span>
      ),
      value: 'reviews',
    },
    { label: tp('tabs.shops'), value: 'shops' },
  ]

  const [ activeTab, setActiveTab ] = useState('about_product')

  return (
    <>
      <TabsSwitcher
        className={styles['root__switcher']}
        tabs={tabs}
        cols={3}
        active={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'about_product' && <About product={product} />}

      {activeTab === 'reviews' && <ProductReviews product={product} />}

      {activeTab === 'shops' && <ShopsWithProduct product={product} />}
    </>
  )
}

export default function ProductDetail ({ product }: ProductDetailProps) {
  const [ showStickyCard, setShowStickyCard ] = useState<boolean>(false)
  const [ thumbsSwiper, setThumbsSwiper ] = useState<SwiperType | null>(null)

  const { isAdult } = useIsAdult()

  const { t: tn } = useTranslation('navigation')
  const { t: tf } = useTranslation('favorites')
  const { t: tp } = useTranslation('product')

  const isTablet = useMediaQuery('(max-width: 991px)')

  const { openGalleryModal } = useGalleryModal()
  const { setImages } = useGalleryStore()

  const { ref: observerRef, isIntersecting } = useIntersectionObserver()

  const galleryImages = getProductGalleryImages(product, false)
  const fullGalleryImages = getProductGalleryImages(product, true)

  const handleClickImage = () => {
    setImages(fullGalleryImages)
    openGalleryModal()
  }

  // TODO: показываем закрепленную карточку
  useEffect(() => {
    setShowStickyCard(!isIntersecting)
  }, [ isIntersecting ])

  return (
    <div className={styles.root}>
      <div className={styles.root__sticky}>
        {showStickyCard && <StickyCard product={product} />}
      </div>

      <Breadcrumbs
        className={styles.root__breadcrumbs}
        links={[
          {
            title: tn('catalog'),
            to: URL_PAGE_CATALOG,
          },
          {
            title: product.name,
          },
        ]}
      />

      <div className={styles.root__grid}>
        <div className={styles.root__photos}>
          <div className={styles['root__photos-main']}>
            <Swiper
              slidesPerView={isTablet ? 1.1 : 1}
              spaceBetween={isTablet ? 16 : 0}
              modules={[ Thumbs ]}
              thumbs={{ swiper: thumbsSwiper }}
            >
              {galleryImages.map((url, index) => (
                <SwiperSlide key={index}>
                  <ImageWithBg
                    className={styles['root__main-photo']}
                    src={url}
                    onClick={handleClickImage}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <BadgeGroup className={styles.root__badges}>
              {product.marketingLabels?.map((item) => (
                <Badge 
                  key={item?.id}
                  backgroundColor={item?.optionBackgroundColor ?? undefined}
                  color={item?.optionColor}
                  image={item?.image?.url}
                />
              ))}
              {!isAdult && (product.is_alcohol || product.min_age) && (
                <Badge
                  title={`${product.min_age ?? '21'}+`}
                  backgroundColor={'#434242'}
                />
              )}
            </BadgeGroup>
          </div>

          {!isTablet && (
            <div className={styles.root__thumbs}>
              <div className={styles['root__thumbs-slider']}>
                <Swiper
                  onSwiper={setThumbsSwiper}
                  spaceBetween={10}
                  slidesPerView="auto"
                  watchSlidesProgress
                  className={styles['root__thumbs-swiper']}
                  direction="vertical"
                >
                  {galleryImages.map((url, index) => (
                    <SwiperSlide
                      key={index}
                      className={styles['root__thumbs-slide']}
                    >
                      <ImageWithBg
                        className={styles['root__thumbs-img']}
                        src={url}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          )}
        </div>

        <div className={styles.root_info}>
          <div className={styles.root__item}>
            <h1 className={styles.root__title}>{product.name}</h1>
            {product.xid && (
              <div className={styles.root__article}>
                Арт.: {product.xid}
              </div>
            )}
            <div className={styles.root__chain}>
              <div className={styles['root__chain-item']}>
                <div className={styles.root__rating}>
                  <RatingPreview
                    className={styles['root__rating-star']}
                    classNameText={styles['root__rating-value']}
                    value={product.average_rating as number}
                  />
                  <span
                    className={cn(styles['root__rating-count'], 'rating-count-global')}
                  >
                    {product.review_count}{' '}
                    {getNoun(
                      product.review_count as number,
                      tp('productReview.1'),
                      tp('productReview.2'),
                      tp('productReview.3'),
                    )}
                  </span>
                </div>
              </div>
              <div className={styles['root__chain-item']}>
                <InStock data={product} />
              </div>
            </div>
          </div>

          <div className={styles.root__item}>
            <div className={styles['root__price-wrapper']}>
              {product.priceOffer?.price && (
                <div
                  className={cn(styles.root__price, {
                    [styles._accent]: !!product.priceOffer.oldPrice,
                  })}
                >
                  {formatPrice(product.priceOffer.price)}{' '}
                  <span>/ {formatPriceAmount({ priceOffer: product.priceOffer, t: tf })}</span>
                </div>
              )}
              {!!product.priceOffer?.oldPrice && (
                <div className={styles['root__old-price']}>
                  {formatPrice(product.priceOffer.oldPrice)}
                </div>
              )}
            </div>
          </div>

          <div
            className={cn(styles.root__item, styles.root__group)}
            ref={observerRef}
          >
            {!!product.stock?.amount && (
              <ControlCart isTablet={false} product={product} />
            )}
            {!product.stock?.amount && (
              <Button size="large" disabled>
                {tp('product.out_of_stock')}
              </Button>
            )}
            <FavoriteButton
              className={styles.root__favorite}
              id={product.id}
              isFavorite={!!product.isFavorite}
              isLarge
              product={product}
            />
          </div>

          <div className={styles.root__item}>
            <ProductProperties product={product} />
          </div>

          {product.ingredients && (
            <div className={cn(styles.root__item, styles.root__ingredients)}>
              <h3>{tp('product.ingredients')}:</h3>
              <p>{product.ingredients}</p>
            </div>
          )}
        </div>
      </div>

      <ProductTabs product={product} />
    </div>
  )
}